import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import StudentInfoCard from "../components/StudentInfoCard";
import UnitCards from "../components/UnitCards";
import FinancialCards from "../components/FinancialCards";
import FeesChart from "../components/FeesChart";
import axios from "axios";
import "../assets/css/studentdashboard.css";

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction
  ? "https://schools.luth.gov.ng"
  : "http://localhost:3003";

const LoadingSpinner = () => (
  <div className="spinner-container">
    <div className="loading-spinner"></div>
  </div>
);

export default function StudentsDashboard() {
  const [student, setStudent] = useState(null);
  const [feeAmount, setFeeAmount] = useState(0);
  const [registeredUnits, setRegisteredUnits] = useState(0);
  const [maxUnits, setMaxUnits] = useState(0);
  const [payments, setPayments] = useState([]);
  const [results, setResults] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState("payments");
  const [filterLevel, setFilterLevel] = useState("All");
  const [filterSession, setFilterSession] = useState("All");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const levels = Array.from(new Set(results.map((r) => r.Level)));
  const sessions = Array.from(new Set(results.map((r) => r.Session)));

  const getCurrentAcademicSession = () => {
    const year = new Date().getFullYear();
    return `${year}/${year + 1}`;
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const token = localStorage.getItem("token");
        const matricNo = localStorage.getItem("matricNo");
        const headers = { Authorization: `Bearer ${token}` };

        if (!matricNo) {
          setError("Matriculation number not found. Please log in again.");
          setLoading(false);
          return;
        }

        const userRes = await axios.get(
          `${API_BASE_URL}/api/users/${matricNo}`,
          { headers },
        );

        let biodata = null;
        try {
          const bioRes = await axios.get(
            `${API_BASE_URL}/api/biodata/${matricNo}`,
            { headers },
          );
          biodata = bioRes.data;
        } catch (bioErr) {
          biodata = null;
        }

        const user = userRes.data || {};
        const bio = biodata || {};

        const currentAcademicSession = getCurrentAcademicSession();

        const level =
          user.StudentLevel || bio.level || localStorage.getItem("level") || "";

        // Session = Semester in your app
        const session = bio.session || localStorage.getItem("session") || "";

        // CalenderYear is kept separate as academic session/year
        const academicSession = user.CalenderYear || currentAcademicSession;

        const programmeName = user.programme || bio.department || "";

        setStudent({
          photo: user.picture || bio.picture || "",
          name: user.fullName || bio.fullName || "Student",
          matricNo: user.matricNo || bio.matricNumber || matricNo,
          regNo: bio.jambRegNumber || "N/A",
          email: user.email || bio.email || "",
          phoneNumber: bio.phoneNumber || "",
          department: user.programme || bio.department || "",
          programme: programmeName,
          level: level || "N/A",
          session: session || "N/A",
          academicSession: academicSession || "N/A",
          entryMode: user.entryMode || bio.entryMode || "UTME",
          entryYear: user.entryYear || bio.entryYear || "N/A",
        });

        // Only fetch fee/course data if we have usable level/session
        if (level && session) {
          const [
            { data: feeData },
            { data: regCourses },
            { data: maxUnitsData },
          ] = await Promise.all([
            axios.get(`${API_BASE_URL}/api/fees`, {
              headers,
              params: {
                programmeName,
                level,
                semester: session, // session and semester are the same in your app
              },
            }),
            axios.get(`${API_BASE_URL}/api/courses/registered`, {
              headers,
              params: {
                matricNo,
                level,
                session, // same value
                academicSession,
              },
            }),
            axios.get(`${API_BASE_URL}/api/courses/maxUnits`, {
              headers,
              params: {
                programmeName,
                level,
                semester: session, // same value
              },
            }),
          ]);

          setFeeAmount(Number(feeData?.feeAmount || 0));
          setRegisteredUnits(
            Array.isArray(regCourses)
              ? regCourses.reduce(
                  (sum, c) => sum + Number(c.CreditUnits || 0),
                  0,
                )
              : 0,
          );
          setMaxUnits(Number(maxUnitsData?.totalUnits || 0));
        } else {
          setFeeAmount(0);
          setRegisteredUnits(0);
          setMaxUnits(0);
        }

        const { data: payHist } = await axios.get(
          `${API_BASE_URL}/api/payments/history`,
          { headers, params: { matricNo } },
        );
        setPayments(Array.isArray(payHist) ? payHist : []);

        const { data: gradeData } = await axios.get(
          `${API_BASE_URL}/api/results/view`,
          { headers, params: { matricNo } },
        );
        setResults(Array.isArray(gradeData) ? gradeData : []);

        const { data: noteData } = await axios.get(
          `${API_BASE_URL}/api/notifications`,
          { headers, params: { matricNo } },
        );
        setNotifications(Array.isArray(noteData) ? noteData : []);
      } catch (e) {
        console.error("Dashboard load error:", e);
        setError("Could not load your dashboard data.");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="error-message">{error}</p>;
  if (!student)
    return <p className="error-message">Student data unavailable.</p>;

  const totalFees = feeAmount || 0;
  const totalPaymentsVal = totalFees * 0.6;
  const totalOutstanding = totalFees - totalPaymentsVal;
  const unusedUnits = maxUnits - registeredUnits;

  return (
    <div className="student-dashboard" style={{ marginTop: "-130px" }}>
      <Sidebar />

      <div className="main-content">
        <div className="top-section">
          <StudentInfoCard student={student} />
          <div className="right-section">
            <UnitCards
              maxUnits={maxUnits}
              registeredUnits={registeredUnits}
              unusedUnits={unusedUnits}
            />
            <div className="financial-section">
              <FinancialCards
                totalFees={totalFees}
                totalPayments={totalPaymentsVal}
                totalOutstanding={totalOutstanding}
              />
              <FeesChart
                programmeName={student.programme}
                level={student.level}
                semester={student.session}
              />
            </div>
          </div>
        </div>

        {/* rest of your component stays the same */}
      </div>
    </div>
  );
}
